import { AuthState } from "@okta/okta-auth-js";
import classNames from "classnames";
import * as React from "react";
import { numberOfEmptyRows } from "../../AppConstants";
import i18n from "../../i18n";
import { EmptyListModal } from "../EmptyListModal/EmptyListModal";
import Pagination from "../Pagination/Index";
import { SkeletonTableRow } from "../SkeletonTableRow";
import { NumberFilter } from "../../Analytics/components/Filters/NumberFilter";
import { TextFilter } from "../../Analytics/components/Filters/TextFilter";
import { DateFilter } from "../../Analytics/components/Filters/DateFilter";
import { SelectFilter } from "../../Analytics/components/Filters/SelectFilter";
import { IFilter } from "../../Responses/duck/Models";
import appHistory from "../../AppHistory";
import { DateTimeFormatter } from "../Util/DateTimeFormatter";
import { HistoricalResponseListModal } from "../../Responses/components/HistoricalResponseListModal";
import { Tooltip } from "react-tooltip";
import "./Responses.scss";

export interface IDeletedResponseListProps {
  list: Api.IPagedResult<Api.IDeletedResponseListViewModel>;
  baseRoute: string;
  authState: AuthState | null;
  handlePageSizeChange: (pageNumber: number, itemPerPage: number, auth: AuthState | null) => any;
  onSortChange?: (orderBy: string) => void;
  orderBy?: string;
  filters: IFilter[];
  filteredResults: Api.IDeletedResponseListViewModel[];
  setFilter: (field: string, value: any, operator?: string) => void;
  clearFilter: (field: string) => void;
  clearAllFilters: () => void;
  onFacetChange?: (propertyName: string) => Promise<any>;
  resetVersionHistory: () => void;
}

interface IDeletedResponseListState {
  openModal: boolean;
  activeResponseId: string;
  activeDocumentId: string;
}

const TABLE_ID = "deleted-table";
const NUM_COLUMNS = 8;

class DeletedResponseList extends React.Component<IDeletedResponseListProps, IDeletedResponseListState> {
  constructor(props: IDeletedResponseListProps) {
    super(props);
    this.state = { openModal: false, activeResponseId: "", activeDocumentId: "" };
    this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
    this.handleClickHeader = this.handleClickHeader.bind(this);
    this.handleFilterApply = this.handleFilterApply.bind(this);
    this.handleFilterClear = this.handleFilterClear.bind(this);
    this.getCategoryOptions = this.getCategoryOptions.bind(this);
    this.handleVersionHistoryClick = this.handleVersionHistoryClick.bind(this);
    this.handleVersionHistoryClose = this.handleVersionHistoryClose.bind(this);
  }

  private handleVersionHistoryClick(responseId: string, documentId: string) {
    this.setState({ openModal: true, activeResponseId: responseId, activeDocumentId: documentId });
  }

  private handleVersionHistoryClose() {
    this.setState({ openModal: false, activeResponseId: "", activeDocumentId: "" });
    this.props.resetVersionHistory();
  }

  public componentDidMount() {
    this.initSortingState();
  }

  private initSortingState() {
    if (this.props.orderBy) {
      const table = document.getElementById(TABLE_ID);
      if (table) {
        const [property, direction] = this.props.orderBy.split(' ');
        const columnHeaders = [].slice.call(table.querySelectorAll('th')) as HTMLElement[];
        for (const ch of columnHeaders) {
          if (ch.getAttribute('data-property') === property) {
            ch.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending');
            break;
          }
        }
      }
    }
  }

  private handlePageSizeChange(pageNumber: number, itemPerPage: number): any {
    this.props.handlePageSizeChange(pageNumber, itemPerPage, this.props.authState);
  }

  private handleClickHeader(event: React.MouseEvent) {
    const target = (event.currentTarget as HTMLElement).closest('th') as HTMLElement;
    if (target && this.props.onSortChange) {
      const property = target.getAttribute('data-property');
      const currentSort = target.getAttribute('aria-sort');

      const table = document.getElementById(TABLE_ID);
      if (table) {
        const columnHeaders = [].slice.call(table.querySelectorAll('th')) as HTMLElement[];
        for (const ch of columnHeaders) {
          if (ch !== target && ch.hasAttribute('aria-sort')) {
            ch.removeAttribute('aria-sort');
          }
        }
      }

      let newDirection = 'desc';
      if (currentSort === 'descending') {
        target.setAttribute('aria-sort', 'ascending');
        newDirection = 'asc';
      } else {
        target.setAttribute('aria-sort', 'descending');
        newDirection = 'desc';
      }

      this.props.onSortChange(`${property} ${newDirection}`);
    }
  }

  private getFilterValue(field: string, operator?: string): any {
    const filter = this.props.filters.find((f: IFilter) =>
      f.field === field && (operator ? f.operator === operator : true)
    );
    return filter?.value;
  }

  private isFilterActive(field: string): boolean {
    return this.props.filters.some((f: IFilter) => f.field === field);
  }

  private handleFilterApply(field: string, value: any, operator: string = 'eq') {
    const finalOperator = (field === 'title' || field === 'createUserName' || field === 'deletedByUserName') && operator === 'eq'
      ? 'contains'
      : operator;
    this.props.setFilter(field, value, finalOperator);
    const newRoute = {
      ...appHistory.location,
      pathname: `${this.props.baseRoute}/1`
    };
    appHistory.replace(newRoute);
  }

  private getCategoryOptions() {
    if (this.props.onFacetChange) {
      return this.props.onFacetChange('category/name')
        .then((response: any) => response)
        .catch((error: any) => {
          console.error(error);
          const categories = Array.from(new Set(this.props.list.results.map(item => item.category)));
          return { facets: [{ groups: categories.map(cat => ({ key: cat })) }] };
        });
    }
    const categories = Array.from(new Set(this.props.list.results.map(item => item.category)));
    return Promise.resolve({ facets: [{ groups: categories.map(cat => ({ key: cat })) }] });
  }

  private handleFilterClear(field: string) {
    this.props.clearFilter(field);
    const newRoute = {
      ...appHistory.location,
      pathname: `${this.props.baseRoute}/1`
    };
    appHistory.replace(newRoute);
  }

  public render() {
    const { list } = this.props;
    const isEmpty = list.results && list.rowCount === 0;
    const hasActiveFilters = this.props.filters && this.props.filters.length > 0;
    const displayResults = this.props.filteredResults.length > 0 ? this.props.filteredResults : list.results;
    const showNoResults = displayResults.length === 0 && hasActiveFilters;

    return (
      <div className="new-table-margin-top published-responses">
        <table
          className={classNames("table", { "table-hover": !isEmpty }, "table-responsive", { "no-results": showNoResults })}
          id={TABLE_ID}
        >
          <thead>
            <tr>
              <th className={classNames("referenceId", { "filter-applied": this.isFilterActive('referenceId') })} scope="col" data-column-index="0" data-property="referenceId">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.idColHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <NumberFilter
                    field={{ name: 'Response ID', property: 'referenceId' }}
                    firstFilterValue={this.getFilterValue('referenceId', 'ge')}
                    secondFilterValue={this.getFilterValue('referenceId', 'le')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                  />
                </span>
              </th>
              <th className={classNames("title", { "filter-applied": this.isFilterActive('title') })} scope="col" data-column-index="1" data-property="title">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.titleColHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <TextFilter
                    field={{ name: 'Response Title', property: 'title' }}
                    value={this.getFilterValue('title')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                  />
                </span>
              </th>
              <th className={classNames("category", { "filter-applied": this.isFilterActive('category/name') })} scope="col" data-column-index="2" data-property="category/name">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.categoryHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <SelectFilter
                    field={{ name: 'Category', property: 'category/name', multiSelection: true }}
                    value={this.getFilterValue('category/name')}
                    asyncloader={this.getCategoryOptions}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                  />
                </span>
              </th>
              <th className={classNames("createdBy", { "filter-applied": this.isFilterActive('createUserName') })} scope="col" data-column-index="3" data-property="createUserName">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.createdByHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <TextFilter
                    field={{ name: 'Created By', property: 'createUserName' }}
                    value={this.getFilterValue('createUserName')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                  />
                </span>
              </th>
              <th className={classNames("createdDate", { "filter-applied": this.isFilterActive('dateCreated') })} scope="col" data-column-index="4" data-property="dateCreated">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.createdDateColHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <DateFilter
                    field={{ name: 'Created Date', property: 'dateCreated' }}
                    firstFilterValue={this.getFilterValue('dateCreated', 'ge')}
                    secondFilterValue={this.getFilterValue('dateCreated', 'le')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                    showTime={false}
                  />
                </span>
              </th>
              <th className={classNames("deletedBy", { "filter-applied": this.isFilterActive('deletedByUserName') })} scope="col" data-column-index="5" data-property="deletedByUserName">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.deletedByHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <TextFilter
                    field={{ name: 'Deleted By', property: 'deletedByUserName' }}
                    value={this.getFilterValue('deletedByUserName')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                  />
                </span>
              </th>
              <th className={classNames("deletedDate", { "filter-applied": this.isFilterActive('dateDeleted') })} scope="col" data-column-index="6" data-property="dateDeleted">
                <span className="published-table-heading">
                  {i18n.t("deletedResponseList.deletedDateColHeading")}
                  <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader} />
                  <DateFilter
                    field={{ name: 'Deleted Date', property: 'dateDeleted' }}
                    firstFilterValue={this.getFilterValue('dateDeleted', 'ge')}
                    secondFilterValue={this.getFilterValue('dateDeleted', 'le')}
                    onFilterApply={this.handleFilterApply}
                    onClear={this.handleFilterClear}
                    showTime={false}
                  />
                </span>
              </th>
              <th className="version-history-col" scope="col" data-column-index="7" />
            </tr>
          </thead>
          <tbody>
            {displayResults && displayResults.length > 0 ? (
              displayResults.map(item => (
                <tr key={item.responseId}>
                  <td>{item.referenceId}</td>
                  <td>{item.title}</td>
                  <td>{item.category}</td>
                  <td>{item.createUserName}</td>
                  <td>{DateTimeFormatter.ToShortDate(item.dateCreated ?? undefined)}</td>
                  <td>{item.deletedByUserName}</td>
                  <td>{DateTimeFormatter.ToShortDate(item.dateDeleted ?? undefined)}</td>
                  <td className="deleted-version-history-col">
                    <img
                      data-tooltip-id="version-history-tooltip"
                      data-tooltip-content="Version History"
                      src="/images/version-history.svg"
                      alt="Version History"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); this.handleVersionHistoryClick(item.responseId, item.panvivaDocumentId.toString()); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); this.handleVersionHistoryClick(item.responseId, item.panvivaDocumentId.toString()); } }}
                    />
                  </td>
                </tr>
              ))
            ) : showNoResults ? (
              <tr className="no-results-row">
                <td colSpan={NUM_COLUMNS} className="no-results-cell">No results found</td>
              </tr>
            ) : (
              [...Array(numberOfEmptyRows)].map((_, i) => <SkeletonTableRow numOfColumns={NUM_COLUMNS} key={i} />)
            )}
          </tbody>
        </table>

        {list && list.rowCount > 0 && (
          <Pagination
            onPageSizeChange={this.handlePageSizeChange}
            pageSize={list.pageSize}
            currentPage={list.currentPage}
            linkUrlLeftPart={this.props.baseRoute}
            totalRecordsNumber={list.rowCount}
            pageCount={list.pageCount}
            authState={this.props.authState}
          />
        )}

        {isEmpty && !hasActiveFilters && (
          <EmptyListModal
            title={i18n.t("deletedResponseList.emptyListHeadingMessage")}
            message={i18n.t("deletedResponseList.emptyListBodyMessage")}
          />
        )}

        {this.state.openModal && (
          <HistoricalResponseListModal
            responseId={this.state.activeResponseId}
            documentId={this.state.activeDocumentId}
            authState={this.props.authState!}
            onCloseIconClick={this.handleVersionHistoryClose}
          />
        )}

        <Tooltip
          id="version-history-tooltip"
          place={"bottom-start"}
          variant={"info"}
          className={"cc-tooltip"} />
      </div>
    );
  }
}

export default DeletedResponseList;
